import React, {useState, useRef, useEffect} from "react";
import {
  Popover,
  Overlay,
  ListGroup,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import "./ModalSelectPort.css";

import routesData, {ferryRoutesGeoJSON} from "./routesDb";

const ModalSelectPort = ({
  show,
  handleCloseModalSelectPort,
  handleSelectPort,
  targetElement,
  selectedPortofLoading,
  isSelectingDestination,
}) => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const ref = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Hilfsfunktion zum Vergleichen von Koordinaten
  const coordinatesMatch = (lng1, lat1, lng2, lat2) => {
    return Math.abs(lng1 - lng2) < 0.0001 && Math.abs(lat1 - lat2) < 0.0001;
  };

  // Finde alle Häfen, die eine Verbindung zum selectedPortofLoading haben
  const getConnectedPorts = (selectedPortofLoading) => {
    if (!selectedPortofLoading) return routesData;

    const connectedPorts = [];

    ferryRoutesGeoJSON.features.forEach((route) => {
      const coordinates = route.geometry.coordinates[0];
      const firstPoint = coordinates[0];
      const lastPoint = coordinates[coordinates.length - 1];

      const isStartPort = coordinatesMatch(
        firstPoint[0],
        firstPoint[1],
        selectedPortofLoading.lng,
        selectedPortofLoading.lat,
      );

      const isEndPort = coordinatesMatch(
        lastPoint[0],
        lastPoint[1],
        selectedPortofLoading.lng,
        selectedPortofLoading.lat,
      );

      if (isStartPort) {
        const destinationPort = routesData.find((p) =>
          coordinatesMatch(p.lng, p.lat, lastPoint[0], lastPoint[1]),
        );
        if (
          destinationPort &&
          !connectedPorts.some((p) =>
            coordinatesMatch(
              p.lng,
              p.lat,
              destinationPort.lng,
              destinationPort.lat,
            ),
          )
        ) {
          connectedPorts.push(destinationPort);
        }
      }

      if (isEndPort) {
        const startPort = routesData.find((p) =>
          coordinatesMatch(p.lng, p.lat, firstPoint[0], firstPoint[1]),
        );
        if (
          startPort &&
          !connectedPorts.some((p) =>
            coordinatesMatch(p.lng, p.lat, startPort.lng, startPort.lat),
          )
        ) {
          connectedPorts.push(startPort);
        }
      }
    });

    return connectedPorts;
  };

  // Bestimme welche Häfen angezeigt werden sollen
  const availablePorts = isSelectingDestination
    ? getConnectedPorts(selectedPortofLoading)
    : routesData;

  // Eindeutige Länder aus den verfügbaren Häfen extrahieren
  const getUniqueCountries = () => {
    const countries = availablePorts.map((port) => port.country);
    return [...new Set(countries)].sort();
  };

  // Häfen nach ausgewähltem Land filtern
  const getPortsByCountry = (country) => {
    return availablePorts.filter((port) => port.country === country);
  };

  // Popper Konfiguration
  const popperConfig = {
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 10],
        },
      },
      {
        name: "preventOverflow",
        options: {
          boundary: "viewport",
          padding: 8,
        },
      },
      {
        name: "flip",
        options: {
          fallbackPlacements: ["top-start", "bottom-start"],
        },
      },
    ],
  };

  const popover = (
    <Popover id="popover-select-port" className="port-selector">
      <Popover.Header className="port-selector-header">
        <div className="header-title">
          {isSelectingDestination ? "Zielhafen wählen" : "Starthafen wählen"}
        </div>
        {isSelectingDestination && selectedPortofLoading && (
          <div className="selected-origin">
            Von: <strong>{selectedPortofLoading.name}</strong>
          </div>
        )}
      </Popover.Header>
      <Popover.Body className="port-selector-body">
        <Container fluid className="selector-container">
          <Row>
            <Col xs={6} className="selector-column">
              <div className="column-header">Land</div>
              <ListGroup className="selector-list">
                {getUniqueCountries().length > 0 ? (
                  getUniqueCountries().map((country) => (
                    <ListGroup.Item
                      key={country}
                      action
                      active={selectedCountry === country}
                      onClick={() => setSelectedCountry(country)}
                      className="selector-item"
                    >
                      {country}
                    </ListGroup.Item>
                  ))
                ) : (
                  <div className="empty-state">Keine Länder verfügbar</div>
                )}
              </ListGroup>
            </Col>

            <Col xs={6} className="selector-column">
              <div className="column-header">Hafen</div>
              <ListGroup className="selector-list">
                {selectedCountry ? (
                  getPortsByCountry(selectedCountry).length > 0 ? (
                    getPortsByCountry(selectedCountry).map((port) => (
                      <ListGroup.Item
                        key={`${port.name}-${port.country}`}
                        action
                        onClick={() => {
                          handleSelectPort(port);
                          setSelectedCountry(null);
                        }}
                        className="selector-item"
                      >
                        {port.name}
                      </ListGroup.Item>
                    ))
                  ) : (
                    <div className="empty-state">Keine Häfen verfügbar</div>
                  )
                ) : (
                  <div className="empty-state">Wähle zuerst ein Land</div>
                )}
              </ListGroup>
            </Col>
          </Row>
        </Container>
      </Popover.Body>
    </Popover>
  );

  return (
    <div ref={ref}>
      <Overlay
        show={show}
        target={targetElement}
        placement={isMobile ? "bottom-start" : "top-start"}
        container={isMobile ? document.body : ref.current || ref}
        rootClose={true}
        onHide={() => {
          handleCloseModalSelectPort();
          setSelectedCountry(null);
        }}
        popperConfig={popperConfig}
      >
        {popover}
      </Overlay>
    </div>
  );
};

export default ModalSelectPort;
